/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, apiUrl } from './config/supabase';
import { SPIN_CSS, PERM_KEYS } from './config/styles';
import { TR, loadCustomLabels, clearLabelsCache } from './translations/tr';
import { useToast } from './hooks/useToast';
import { useData } from './hooks/useData';

// Layout
import Header          from './components/layout/Header';
import FloatingButtons from './components/layout/FloatingButtons';
import NewsTicker      from './components/layout/NewsTicker';
import SidebarPanels   from './components/layout/SidebarPanels';

// Notifications
import { ToastContainer, NotifPanel } from './components/notifications';

// Pages
import Home                      from './components/pages/Home';
import Academy                   from './components/pages/Academy';
import Instructors               from './components/pages/Instructors';
import { Contact }  from './components/pages/pages';
import Agency       from './components/pages/Agency';
import { Player }           from './components/pages/Player';
import { MyCourses }        from './components/pages/MyCourses';
import { Dashboard, LessonsManager } from './components/pages/Dashboard';
import BotManager from './components/pages/BotManager';
import BotSubscribe from './components/pages/BotSubscribe';   // 🤖 صفحة اشتراك المساعد (بعد السنة المجانية)   // 🤖 مدير المساعد (ويب + تليجرام) — بيحتوي قاعدة المعرفة
import Reports                               from './components/pages/reports/index';
import ReportBuilder                         from './components/pages/reports/ReportBuilder';
import AboutSection                          from './components/pages/AboutSection';
import LabelsManager                         from './components/settings/LabelsManager';
import { logUserActivity, logEmployeeActivity, logEmployeeLogout } from './hooks/useActivityLog';
import ForgotPasswordModal from './modals/ForgotPasswordModal';

// Modals
import {
  EmpLoginModal, CustModal, PaymentModal,
  CourseModal, LessonModal, ServiceModal,
  RoleModal, EmployeeModal, SettingsModal,
  CouponModal, CategoryModal, InstructorModal,
} from './components/modals';

export default function App() {
  // ── data ──
  const data = useData();
  const { toasts, showToast, removeToast } = useToast();

  // ── auth ──
  const [employee, setEmployee] = useState(() => { try { return JSON.parse(localStorage.getItem('emp')); } catch { return null; } });
  const [customer, setCustomer] = useState(() => { try { return JSON.parse(localStorage.getItem('cust')); } catch { return null; } });
  useEffect(() => { localStorage.setItem('emp',  JSON.stringify(employee)); }, [employee]);
  useEffect(() => {
    localStorage.setItem('cust', JSON.stringify(customer));
    if (customer?.id) data.loadProgress(customer.id);
  }, [customer?.id]); // eslint-disable-line

  // ── nav ──
  const [lang,           setLang]           = useState('ar');
  const [customLabels,   setCustomLabels]   = useState([]);
  const [page,           setPage]           = useState('home');
  const [focusInstructorId, setFocusInstructorId] = useState(null);
  const [activeCourse,   setActiveCourse]   = useState(null);
  const [activeLesson,   setActiveLesson]   = useState(null);
  const [managingCourse, setManagingCourse] = useState(null);

  // ── modals state ──
  const [showEmpLogin,      setShowEmpLogin]      = useState(false);
  const [showCustModal,     setShowCustModal]      = useState(false);
  const [custTab,           setCustTab]            = useState('login');
  const [showPayModal,      setShowPayModal]       = useState(false);
  const [showCourseModal,   setShowCourseModal]    = useState(false);
  const [showServiceModal,  setShowServiceModal]   = useState(false);
  const [showLessonModal,   setShowLessonModal]    = useState(false);
  const [showSettingsModal, setShowSettingsModal]  = useState(false);
  const [showEmpModal,      setShowEmpModal]       = useState(false);
  const [showRoleModal,     setShowRoleModal]      = useState(false);
  const [showForgotPass,    setShowForgotPass]     = useState(false);
  const [showCouponModal,   setShowCouponModal]    = useState(false);
  const [showCategoryModal, setShowCategoryModal]  = useState(false);
  const [showInstructorModal, setShowInstructorModal] = useState(false);
  const [editingCategory,   setEditingCategory]    = useState(null);
  const [editingInstructor, setEditingInstructor]  = useState(null);
  const [showLabelsManager, setShowLabelsManager]  = useState(false);
  const [showKnowledge,     setShowKnowledge]      = useState(false);
  const [editingCoupon,     setEditingCoupon]      = useState(null);
  const [editingCourse,     setEditingCourse]      = useState(null);
  const [editingService,    setEditingService]     = useState(null);
  const [editingLesson,     setEditingLesson]      = useState(null);
  const [editingEmp,        setEditingEmp]         = useState(null);
  const [editingRole,       setEditingRole]        = useState(null);

  // ── forms ──
  const empUsernameRef = useRef(null);
  const empPasswordRef = useRef(null);
  const logoClickCount = useRef(0);
  const logoClickTimer = useRef(null);
  const secretBuffer   = useRef('');
  const secretTimer    = useRef(null);
  const [empPerms,  setEmpPerms]  = useState({});
  const [rolePerms, setRolePerms] = useState({});
  const [custForm,  setCustForm]  = useState({ name: '', email: '', phone: '', password: '', username: '' });
  const [custErr,   setCustErr]   = useState('');
  const [custOk,    setCustOk]    = useState('');
  const [custLoad,  setCustLoad]  = useState(false);
  const [payLoad,   setPayLoad]   = useState(false);
  const [payErr,    setPayErr]    = useState('');
  const [saveLoad,  setSaveLoad]  = useState(false);
  const [empErr,    setEmpErr]    = useState('');
  const [empOk,     setEmpOk]     = useState('');
  const [empLoad,   setEmpLoad]   = useState(false);

  // ── notifications ──
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const lastNotifRead    = useRef(sessionStorage.getItem('notifRead') || '');
  // دفعات في الانتظار: نستبعد دفعات الكارت المعلّقة القديمة (أكتر من ساعة)
  // لأن جلسة Paymob بتنتهي بعد ساعة — فأي كارت لسه pending بعد كده يبقى الدفع ما اكتملش
  // التحويلات اليدوية (transfer/instapay/vodafone) بتفضل ظاهرة دايمًا لأنها محتاجة تفعيل يدوي
  const pendingPayments  = data.payments.filter(p => {
    if (p.status !== 'pending') return false;
    if (p.method === 'card') {
      const ageMs = Date.now() - new Date(p.created_at).getTime();
      const ONE_HOUR = 60 * 60 * 1000;
      // كارت مكتمل (عنده transaction_id) أو لسه جديد (أقل من ساعة) → نعرضه
      // كارت قديم من غير transaction_id → دفع ما اكتملش، نخفيه
      if (!p.paymob_transaction_id && ageMs > ONE_HOUR) return false;
    }
    return true;
  });
  const pendingReviews   = data.reviews.filter(r => r.is_approved !== true);
  const unreadCount      = pendingPayments.length + pendingReviews.length + data.contactMessages.filter(m => {
    if (!lastNotifRead.current) return true;
    return new Date(m.created_at) > new Date(lastNotifRead.current);
  }).length;

  const openNotifPanel = () => {
    setShowNotifPanel(x => !x);
    if (!showNotifPanel) {
      const now = new Date().toISOString();
      lastNotifRead.current = now;
      sessionStorage.setItem('notifRead', now);
    }
  };

  // ── computed ──
  const isRTL = lang === 'ar';

  // ── تزامن لغة/اتجاه الـ document مع اللغة المختارة + منع ترجمة جوجل ──
  // (كان lang="en" ثابت فكروم بيقترح ترجمة العربي "للعربي" ويبوّظ المسميات)
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('translate', 'no');
  }, [lang, isRTL]);
  const t     = TR(lang, data.settings.site_name, customLabels);
  const perms = employee ? {
    can_manage_courses:   employee.can_manage_courses   ?? false,
    can_manage_services:  employee.can_manage_services  ?? false,
    can_manage_settings:  employee.can_manage_settings  ?? false,
    can_view_dashboard:   employee.can_view_dashboard   ?? false,
    can_view_customers:   employee.can_view_customers   ?? false,
    can_manage_employees: employee.can_manage_employees ?? false,
    can_manage_lessons:   employee.can_manage_lessons   ?? false,
    can_view_enrollments: employee.can_view_enrollments ?? false,
  } : {};

  // ── load on mount ──
  useEffect(() => { data.loadAll(); }, []);

  // ── إدارة انتهاء جلسة الموظف (التوكن صالح 12 ساعة) ──────────
  // بنقرا exp من التوكن (من غير تحقق توقيع — التحقق الحقيقي على السيرفر)
  // عشان نعرف الجلسة خلصت ولا لأ، فنبلّغ المستخدم بدل ما نوريه جداول فاضية.
  const isEmpTokenValid = () => {
    try {
      const tok = localStorage.getItem('emp_token') || '';
      if (!tok) return false;
      let body = tok.split('.')[0].replace(/-/g, '+').replace(/_/g, '/');
      while (body.length % 4) body += '=';
      const payload = JSON.parse(atob(body));
      return !!payload.exp && Date.now() < payload.exp;
    } catch { return false; }
  };
  const clearEmpSession = (msg) => {
    let had = false;
    try { had = !!localStorage.getItem('emp_token'); } catch {}
    try { if (!had) had = !!JSON.parse(localStorage.getItem('emp')); } catch {}
    try { localStorage.removeItem('emp_token'); } catch {}
    setEmployee(null);
    if (!had) return; // مكنش فيه جلسة أصلًا — مفيش داعي لرسالة
    setPage('home');
    if (msg) showToast(msg, 'warning');
    setShowEmpLogin(true);
  };
  // على فتح الصفحة: لو فيه موظف مخزّن بس التوكن خلص/مفقود → نظّف وبلّغ
  useEffect(() => {
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem('emp')); } catch {}
    if (stored && !isEmpTokenValid()) clearEmpSession('انتهت الجلسة، سجّل دخول تاني');
  }, []); // eslint-disable-line
  // أثناء الاستخدام: لو أي طلب إداري رجّع 401 → نفس المعالجة
  useEffect(() => {
    const onExpire = () => clearEmpSession('انتهت الجلسة، سجّل دخول تاني');
    window.addEventListener('emp-session-expired', onExpire);
    return () => window.removeEventListener('emp-session-expired', onExpire);
  }, []); // eslint-disable-line

  // ── إدارة انتهاء جلسة العميل (نفس فكرة الموظفين) ──────────
  // العميل بيفضل "شكله مسجّل" بس التوكن بينتهي بعد 12 ساعة →
  // فكان بيشوف "لازم تشترك" وهو مشترك فعلاً. هنا بننظّف ونطلب دخول تاني.
  const isCustTokenValid = () => {
    try {
      const tok = localStorage.getItem('cust_token') || '';
      if (!tok) return false;
      let body = tok.split('.')[0].replace(/-/g, '+').replace(/_/g, '/');
      while (body.length % 4) body += '=';
      const payload = JSON.parse(atob(body));
      return !!payload.exp && Date.now() < payload.exp;
    } catch { return false; }
  };
  const clearCustSession = () => {
    try { localStorage.removeItem('cust_token'); } catch {}
    setCustomer(null);
    showToast('انتهت جلستك، سجّل دخول تاني', 'warning');
    setShowCustModal(true); setCustTab('login'); setCustErr('');
    setCustForm({ name: '', email: '', phone: '', password: '', username: '' });
  };
  // على فتح الصفحة: عميل مخزّن (مش زائر) بس التوكن منتهي/مفقود → نظّف واطلب دخول
  useEffect(() => {
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem('cust')); } catch {}
    if (stored && !stored.isGuest && !isCustTokenValid()) clearCustSession();
  }, []); // eslint-disable-line
  // أثناء الاستخدام: مشغّل الفيديو (أو غيره) اكتشف انتهاء الجلسة
  useEffect(() => {
    const onExpire = () => clearCustSession();
    window.addEventListener('cust-session-expired', onExpire);
    return () => window.removeEventListener('cust-session-expired', onExpire);
  }, []); // eslint-disable-line

  // ── 🤖 ربط تليجرام بحساب الويب: /link-telegram?code=XXXX ─────────
  // البوت على تليجرام بيبعت اللينك ده للعميل. لو مسجّل دخول → نربط فورًا،
  // لو لأ → نفتح نافذة الدخول، وأول ما يسجّل نربط تلقائي.
  // deep link: /?page=bot-subscribe (من رسائل التجديد على تليجرام)
  useEffect(() => {
    try {
      const u = new URL(window.location.href);
      const pg = u.searchParams.get('page');
      if (pg === 'bot-subscribe') { setPage('bot-subscribe'); window.history.replaceState({}, '', '/'); }
    } catch {}
  }, []); // eslint-disable-line

  const [tgLinkCode, setTgLinkCode] = useState(() => {
    try {
      const u = new URL(window.location.href);
      if (u.pathname.replace(/\/$/, '') === '/link-telegram' || u.searchParams.get('tg_link')) {
        return (u.searchParams.get('code') || u.searchParams.get('tg_link') || '').toUpperCase();
      }
    } catch {}
    return '';
  });
  useEffect(() => {
    if (!tgLinkCode) return;
    try { window.history.replaceState({}, '', '/'); } catch {}
    if (!customer || customer.isGuest || !isCustTokenValid()) {
      showToast('سجّل دخول بحسابك عشان نربطه ببوت تليجرام 🤖', 'info');
      setShowCustModal(true); setCustTab('login'); setCustErr('');
      return;
    }
    (async () => {
      let token = '';
      try { token = localStorage.getItem('cust_token') || ''; } catch {}
      try {
        const res = await fetch(apiUrl('/api/bot/link/consume'), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, code: tgLinkCode }),
        });
        const r = await res.json().catch(() => ({}));
        if (res.ok && r.ok) showToast('✅ تم ربط حسابك ببوت تليجرام — ارجع للبوت وكمّل', 'success');
        else showToast(r.message || 'فشل ربط تليجرام', 'error');
      } catch { showToast('فشل الاتصال بالسيرفر', 'error'); }
      setTgLinkCode('');
    })();
  }, [tgLinkCode, customer?.id]); // eslint-disable-line

  // تحميل المسميات المخصّصة من قاعدة البيانات (للترجمة)
  const refreshLabels = useCallback(async () => {
    clearLabelsCache();
    const rows = await loadCustomLabels();
    setCustomLabels(rows || []);
  }, []);
  useEffect(() => { refreshLabels(); }, [refreshLabels]);

  // ══════════════════════════════════════════
  // SECRET SHORTCUTS
  // ══════════════════════════════════════════
  const openEmpLogin = useCallback(() => {
    setEmpErr(''); setEmpOk('');
    setShowEmpLogin(p => !p);
  }, []);

  const handleLogoClick = useCallback(() => {
    logoClickCount.current++;
    if (logoClickTimer.current) clearTimeout(logoClickTimer.current);
    logoClickTimer.current = setTimeout(() => { logoClickCount.current = 0; }, 1500);
    if (logoClickCount.current >= 5) { logoClickCount.current = 0; openEmpLogin(); }
  }, [openEmpLogin]);

  const onKey = useCallback((e) => {
    if (e.ctrlKey && e.altKey && (e.key === 'a' || e.key === 'A')) { e.preventDefault(); openEmpLogin(); return; }
    if (e.key === 'F8') { e.preventDefault(); openEmpLogin(); return; }
    if (!e.ctrlKey && !e.altKey && !e.metaKey && e.key.length === 1) {
      secretBuffer.current += e.key.toLowerCase();
      if (secretBuffer.current.length > 7) secretBuffer.current = secretBuffer.current.slice(-7);
      if (secretTimer.current) clearTimeout(secretTimer.current);
      secretTimer.current = setTimeout(() => { secretBuffer.current = ''; }, 2000);
      if (secretBuffer.current === 'gawhara') { secretBuffer.current = ''; openEmpLogin(); }
    }
  }, [openEmpLogin]);
  useEffect(() => { window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey); }, [onKey]);

  // ══════════════════════════════════════════
  // AUTH HANDLERS
  // ══════════════════════════════════════════
  const handleEmpLogin = async (e) => {
    e.preventDefault();
    const username = empUsernameRef.current?.value?.trim() || '';
    const password = empPasswordRef.current?.value || '';
    setEmpErr(''); setEmpLoad(true);
    try {
      const res = await fetch(apiUrl('/api/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'employee', username, password }),
      });
      const r = await res.json().catch(() => ({}));
      if (!res.ok || !r.ok) { setEmpErr(r.message || t.wrongEmp); setEmpLoad(false); return; }
      const d = r.user;
      try { localStorage.setItem('emp_token', r.token || ''); } catch {}
      data.loadAdminData();
      setEmpOk(t.empOk);
      setTimeout(() => {
        setEmployee({ ...d, role: d.roles });
        setShowEmpLogin(false); setEmpOk('');
        setPage('dashboard');
        showToast(lang === 'ar' ? `مرحباً ${d.name_ar} 👋` : `Welcome ${d.name_en} 👋`);
        logEmployeeActivity({ employeeId: d.id, employeeName: d.name_ar, employeeRole: d.roles?.label_ar, action: 'login' });
      }, 900);
    } catch {
      setEmpErr(lang === 'ar' ? 'فشل الاتصال بالسيرفر' : 'Connection failed');
    }
    setEmpLoad(false);
  };

  const handleCustSubmit = async (e) => {
    e.preventDefault(); setCustErr(''); setCustLoad(true);
    if (custTab === 'guest') {
      setCustomer({ id: 'guest_' + Date.now(), name: custForm.name, phone: custForm.phone, email: '', isGuest: true });
      setCustOk(t.guestBtn + ' ✓');
      setTimeout(() => { setShowCustModal(false); setCustOk(''); setCustForm({ name: '', email: '', phone: '', password: '', username: '' }); }, 900);
      setCustLoad(false); return;
    }
    if (custTab === 'login') {
      const username = (custForm.username || '').trim().toLowerCase();
      if (!username) { setCustErr('أدخل اسم المستخدم'); setCustLoad(false); return; }
      try {
        const res = await fetch(apiUrl('/api/login'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'customer', username, password: custForm.password }),
        });
        const r = await res.json().catch(() => ({}));
        if (!res.ok || !r.ok) {
          setCustErr(r.message || t.wrongCust);
        } else {
          const d = r.user;
          try { localStorage.setItem('cust_token', r.token || ''); } catch {}
          setCustomer(d); setCustOk(t.custOk);
          logUserActivity({ customerId: d.id, customerName: d.name, customerEmail: d.email, action: 'login' });
          setTimeout(() => { setShowCustModal(false); setCustOk(''); setCustForm({ name: '', email: '', phone: '', password: '', username: '' }); }, 900);
        }
      } catch {
        setCustErr(lang === 'ar' ? 'فشل الاتصال بالسيرفر' : 'Connection failed');
      }
    } else {
      const username = (custForm.username || '').trim().toLowerCase();
      if (!username || username.length < 3) { setCustErr('اسم المستخدم لازم يكون 3 أحرف على الأقل'); setCustLoad(false); return; }
      try {
        const res = await fetch(apiUrl('/api/register'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: custForm.name, email: custForm.email.trim(), phone: custForm.phone, username, password: custForm.password }),
        });
        const r = await res.json().catch(() => ({}));
        if (!res.ok || !r.ok) {
          setCustErr(r.message || (lang === 'ar' ? 'حصل خطأ في التسجيل' : 'Registration failed'));
        } else {
          const d = r.user;
          try { localStorage.setItem('cust_token', r.token || ''); } catch {}
          setCustomer(d); data.setCustomers(p => [d, ...p]);
          logUserActivity({ customerId: d.id, customerName: d.name, customerEmail: d.email, action: 'register' });
          setCustOk(t.regOk);
          setTimeout(() => { setShowCustModal(false); setCustOk(''); setCustForm({ name: '', email: '', phone: '', password: '', username: '' }); }, 900);
        }
      } catch {
        setCustErr(lang === 'ar' ? 'فشل الاتصال بالسيرفر' : 'Connection failed');
      }
    }
    setCustLoad(false);
  };

  // ══════════════════════════════════════════
  // PAYMENT HANDLERS
  // ══════════════════════════════════════════
  // ✅ اتنقل سيرفر سايد — الـ Paymob API Key مبقاش في الفرونت إند خالص.
  // السيرفر بيحسب السعر من الـ DB ويتحقق من الكوبون ويسجل الـ payment
  // ويزود عداد الكوبون — كل ده في /api/paymob/init.
  const startCardPayment = async (course, finalPrice, couponData) => {
    setPayLoad(true); setPayErr('');
    try {
      const res = await fetch(apiUrl('/api/paymob/init'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          courseId:   course.id,
          customerId: customer.id,
          couponCode: couponData?.code || null,
        }),
      });
      const result = await res.json();
      if (!res.ok || !result.iframeUrl) {
        setPayErr(result.message || 'حدث خطأ، حاول مرة أخرى');
        setPayLoad(false);
        return;
      }
      setShowPayModal(false);
      window.open(result.iframeUrl, '_blank');
    } catch { setPayErr('حدث خطأ، حاول مرة أخرى'); }
    setPayLoad(false);
  };

  const startManualPayment = async (course, method, note, finalPrice, couponData) => {
    setPayLoad(true); setPayErr('');
    const r = await data.addPayment({
      courseId:   course.id,
      customerId: customer.id,
      method,
      note,
      couponCode: couponData?.code || null,
    });
    if (r.error) { setPayErr(r.error); setPayLoad(false); return; }
    setShowPayModal(false); showToast(t.pendingMsg, 'info');
    setPayLoad(false);
  };

  // ✅ تفعيل مجاني — لما الكوبون يخلي السعر صفر.
  // التحقق إن السعر صفر فعلاً بيتم على السيرفر في /api/enroll-free.
  const startFreeEnroll = async (course, couponData) => {
    setPayLoad(true); setPayErr('');
    try {
      const res = await fetch(apiUrl('/api/enroll-free'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          courseId:   course.id,
          customerId: customer.id,
          couponCode: couponData?.code || null,
        }),
      });
      const result = await res.json();
      if (!res.ok || !result.ok) {
        setPayErr(result.message || (lang === 'ar' ? 'حصل خطأ، حاول تاني' : 'Something went wrong'));
        setPayLoad(false);
        return;
      }
      await data.loadAll();              // حدّث البيانات عشان الاشتراك يظهر فورًا
      setShowPayModal(false);
      showToast(lang === 'ar' ? 'تم تفعيل الكورس مجانًا 🎉' : 'Course activated for free 🎉', 'success');
    } catch {
      setPayErr(lang === 'ar' ? 'فشل الاتصال بالسيرفر' : 'Connection failed');
    }
    setPayLoad(false);
  };

  const handleToggleCustomer = async (customer) => {
    const newStatus = customer.is_active === false ? true : false;
    const token = (() => { try { return localStorage.getItem('emp_token') || ''; } catch { return ''; } })();
    try {
      const res = await fetch(apiUrl('/api/admin/customer-toggle'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, customerId: customer.id, isActive: newStatus }),
      });
      const r = await res.json().catch(() => ({}));
      if (!res.ok || !r.ok) { showToast(r.message || 'فشل تحديث الحساب', 'error'); return; }
      data.setCustomers(p => p.map(c => c.id === customer.id ? { ...c, is_active: newStatus } : c));
      showToast(newStatus ? '✅ تم تفعيل الحساب' : '⏸ تم إيقاف الحساب');
    } catch {
      showToast('فشل الاتصال بالسيرفر', 'error');
    }
  };

  const handleDeleteCustomer = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا العميل؟ سيتم حذف كل بياناته.')) return;
    try {
      const token = (() => { try { return localStorage.getItem('emp_token') || ''; } catch { return ''; } })();
      const res = await fetch(apiUrl('/api/admin/delete-customer'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, customerId: id }),
      });
      const r = await res.json().catch(() => ({}));
      if (!res.ok || !r.ok) { showToast('❌ ' + (r.message || 'فشل الحذف'), 'error'); return; }
      data.setCustomers(p => p.filter(c => c.id !== id));
      showToast('🗑 تم حذف العميل', 'warning');
    } catch {
      showToast('❌ فشل الاتصال بالسيرفر', 'error');
    }
  };

  const handleApprove = async (payment) => {
    const res = await data.approvePayment(payment);
    if (res && res.error) {
      showToast((lang === 'ar' ? '❌ فشل التفعيل: ' : '❌ Activation failed: ') + res.error, 'error');
      return;
    }
    showToast(lang === 'ar' ? '✅ تم تفعيل الكورس بنجاح' : '✅ Course activated');
    setShowNotifPanel(false);
  };

  const handleReject = async (id) => {
    const r = await data.rejectPayment(id);
    if (r && r.error) { showToast('❌ ' + r.error, 'error'); return; }
    showToast(lang === 'ar' ? 'تم رفض الدفع' : 'Payment rejected', 'warning');
    setShowNotifPanel(false);
  };

  // ══════════════════════════════════════════
  // CRUD HANDLERS (form → hook)
  // ══════════════════════════════════════════
  const submitCourse = async (e, thumbnailUrl, avatarUrl, bannerUrl) => {
    e.preventDefault(); setSaveLoad(true);
    const fd = new FormData(e.target);
    const featuresRaw = (fd.get('features') || '').toString().split('\n').map(s => s.trim()).filter(Boolean);
    await data.saveCourse({
      name_ar: fd.get('nameAr'), name_en: fd.get('nameEn'),
      price: Number(fd.get('price')), icon: fd.get('icon'),
      drive_intro_id: fd.get('driveIntro'), thumbnail_url: thumbnailUrl || null,
      category_id: fd.get('categoryId') ? Number(fd.get('categoryId')) : null,
      instructor_id: fd.get('instructorId') ? Number(fd.get('instructorId')) : null,
      description_ar: fd.get('descriptionAr') || null,
      features: featuresRaw,
      instructor_name: fd.get('instructorName') || null,
      instructor_bio: fd.get('instructorBio') || null,
      instructor_avatar: avatarUrl || null,
      bonus_students: Number(fd.get('bonusStudents')) || 0,
      schedule_text_ar: fd.get('scheduleText') || null,
      banner_url: bannerUrl || null,
    }, editingCourse);
    setSaveLoad(false); setShowCourseModal(false); setEditingCourse(null);
    showToast(editingCourse ? (lang === 'ar' ? 'تم تعديل الكورس' : 'Course updated') : (lang === 'ar' ? 'تم إضافة الكورس' : 'Course added'));
  };

  const submitLesson = async (e, attachments = []) => {
    e.preventDefault(); setSaveLoad(true);
    const fd = new FormData(e.target);
    const lr = await data.saveLesson({ course_id: managingCourse.id, title_ar: fd.get('titleAr'), title_en: fd.get('titleEn'), drive_file_id: fd.get('driveId'), vimeo_id: fd.get('vimeoId') || null, bunny_video_id: fd.get('bunnyVideoId') || null, video_source: fd.get('videoSource') || null, order_num: Number(fd.get('orderNum')), duration: fd.get('duration'), is_free: fd.get('isFree') === 'on', description_ar: fd.get('descriptionAr') || null, attachments }, editingLesson);
    setSaveLoad(false);
    if (lr && lr.error) { showToast(lr.error, 'error'); return; }
    setShowLessonModal(false); setEditingLesson(null);
    showToast(editingLesson ? (lang === 'ar' ? 'تم تعديل الحلقة' : 'Lesson updated') : (lang === 'ar' ? 'تم إضافة الحلقة' : 'Lesson added'));
  };

  const submitCategory = async (e) => {
    e.preventDefault(); setSaveLoad(true);
    const fd = new FormData(e.target);
    await data.saveCategory({
      name_ar: fd.get('nameAr'), name_en: fd.get('nameEn') || null,
      icon: fd.get('icon') || '📚', order_num: Number(fd.get('orderNum')) || 0,
      description_ar: fd.get('descriptionAr') || null,
    }, editingCategory);
    setSaveLoad(false); setShowCategoryModal(false); setEditingCategory(null);
    showToast(editingCategory ? 'تم تعديل التصنيف' : 'تم إضافة التصنيف');
  };

  const submitInstructor = async (e, avatarUrl) => {
    e.preventDefault(); setSaveLoad(true);
    const fd = new FormData(e.target);
    await data.saveInstructor({
      name_ar: fd.get('nameAr'), name_en: fd.get('nameEn') || null,
      avatar_url: avatarUrl || null,
      title_ar: fd.get('titleAr') || null,
      bio_ar: fd.get('bioAr') || null,
      experience_ar: fd.get('experienceAr') || null,
      specialties: fd.get('specialties') || null,
      order_num: Number(fd.get('orderNum')) || 0,
    }, editingInstructor);
    setSaveLoad(false); setShowInstructorModal(false); setEditingInstructor(null);
    showToast(editingInstructor ? 'تم تعديل المدرب' : 'تم إضافة المدرب');
  };

  const submitService = async (e, extra = {}) => {
    e.preventDefault(); setSaveLoad(true);
    const fd = new FormData(e.target);
    await data.saveService({
      name_ar:        fd.get('nameAr'),
      name_en:        fd.get('nameEn'),
      price:          Number(fd.get('price')),
      icon:           fd.get('icon'),
      duration_ar:    fd.get('durationAr') || null,
      description_ar: fd.get('descriptionAr') || null,
      video_url:      fd.get('videoUrl') || null,
      features:       extra.features || [],
      is_featured:    extra.is_featured || false,
    }, editingService);
    setSaveLoad(false); setShowServiceModal(false); setEditingService(null);
    showToast(editingService ? (lang === 'ar' ? 'تم تعديل الخدمة' : 'Service updated') : (lang === 'ar' ? 'تم إضافة الخدمة' : 'Service added'));
  };

  const submitSettings = async (e, instapayList, tickerItems) => {
    e.preventDefault(); setSaveLoad(true);
    const fd = new FormData(e.target);
    const r = await data.saveSettings({
      site_name:        fd.get('siteName'),
      logo_url:         fd.get('logoUrl') || '',
      whatsapp_number:  fd.get('whatsappNumber'),
      whatsapp_message: fd.get('whatsappMessage'),
      vodafone_number:  fd.get('vodafoneNumber'),
      instapay_number:  instapayList?.[0]?.number || fd.get('instapayNumber') || '',
      instapay_numbers: instapayList || [],
      bank_account:     fd.get('bankAccount'),
      email_contact:    fd.get('emailContact'),
      address:          fd.get('address'),
      ticker_enabled:   fd.get('tickerEnabled') === 'on',
      ticker_items:     Array.isArray(tickerItems) ? tickerItems : (data.settings.ticker_items || []),
      about_enabled:    fd.get('aboutEnabled') === 'on',
      about_title_ar:   fd.get('aboutTitleAr') || 'من نحن',
      about_title_en:   fd.get('aboutTitleEn') || 'About Us',
      about_text_ar:    fd.get('aboutTextAr') || '',
      about_text_en:    fd.get('aboutTextEn') || '',
      about_stats:      (() => { try { return JSON.parse(fd.get('aboutStats') || '[]'); } catch { return []; } })(),
    });
    setSaveLoad(false);
    if (r && r.error) { showToast(r.error, 'error'); return; }
    setShowSettingsModal(false);
    showToast(lang === 'ar' ? 'تم حفظ الإعدادات ⚙️' : 'Settings saved ⚙️');
  };

  const submitEmployee = async (e) => {
    e.preventDefault(); setSaveLoad(true);
    const fd = new FormData(e.target);
    const p = { username: fd.get('username'), name_ar: fd.get('nameAr'), name_en: fd.get('nameEn'), role_id: Number(fd.get('roleId')), is_active: fd.get('isActive') === 'on', ...empPerms };
    if (fd.get('password')) p.password = fd.get('password');
    const result = await data.saveEmployee(p, editingEmp);
    setSaveLoad(false);
    if (result && result.error) { showToast('❌ ' + result.error, 'error'); return; }
    setShowEmpModal(false); setEditingEmp(null);
    showToast(editingEmp ? (lang === 'ar' ? 'تم تعديل الموظف' : 'Employee updated') : (lang === 'ar' ? 'تم إضافة الموظف' : 'Employee added'));
  };

  const submitCoupon = async (e) => {
    e.preventDefault(); setSaveLoad(true);
    const fd = new FormData(e.target);
    const payload = {
      code:       fd.get('code').toUpperCase().trim(),
      type:       fd.get('type'),
      value:      Number(fd.get('value')),
      course_id:  fd.get('course_id') || null,
      max_uses:   fd.get('max_uses') ? Number(fd.get('max_uses')) : null,
      expires_at: fd.get('expires_at') ? new Date(fd.get('expires_at')).toISOString() : null,
      is_active:  fd.get('is_active') === 'on',
    };
    const token = (() => { try { return localStorage.getItem('emp_token') || ''; } catch { return ''; } })();
    try {
      const res = await fetch(apiUrl('/api/admin/coupon-save'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, coupon: { ...payload, id: editingCoupon ? editingCoupon.id : null } }),
      });
      const r = await res.json().catch(() => ({}));
      if (!res.ok || !r.ok) { setSaveLoad(false); showToast(r.message || 'فشل حفظ الكوبون', 'error'); return; }
    } catch {
      setSaveLoad(false); showToast('فشل الاتصال بالسيرفر', 'error'); return;
    }
    setSaveLoad(false); setShowCouponModal(false); setEditingCoupon(null);
    showToast(editingCoupon ? 'تم تعديل الكوبون ✅' : 'تم إضافة الكوبون 🎟️');
  };

  const submitRole = async (e) => {
    e.preventDefault(); setSaveLoad(true);
    const fd = new FormData(e.target);
    await data.saveRole({ name: fd.get('roleName'), label_ar: fd.get('labelAr'), label_en: fd.get('labelEn'), ...rolePerms }, editingRole);
    setSaveLoad(false); setShowRoleModal(false); setEditingRole(null);
    showToast(editingRole ? (lang === 'ar' ? 'تم تعديل الوظيفة' : 'Role updated') : (lang === 'ar' ? 'تم إضافة الوظيفة' : 'Role added'));
  };

  const handleDeleteEmp = async (id) => {
    if (id === employee?.id) { showToast(t.cannotDelSelf, 'error'); return; }
    if (!window.confirm(t.confirmDel)) return;
    const r = await data.deleteEmployee(id);
    if (r && r.error) { showToast('❌ ' + r.error, 'error'); return; }
    showToast(lang === 'ar' ? 'تم حذف الموظف' : 'Employee deleted', 'warning');
  };

  const handleToggleEmp = async (emp) => {
    const d = await data.toggleEmployeeStatus(emp);
    if (d) showToast(d.is_active ? (lang === 'ar' ? 'تم تفعيل الموظف' : 'Employee activated') : (lang === 'ar' ? 'تم إيقاف الموظف' : 'Employee suspended'), d.is_active ? 'success' : 'warning');
    else showToast(lang === 'ar' ? '❌ فشل تحديث الموظف' : 'Update failed', 'error');
  };

  // ── emp perms helpers ──
  const openAddEmp = () => {
    setEditingEmp(null);
    const ps = {}; PERM_KEYS.forEach(k => ps[k] = data.roles[0]?.[k] || false);
    setEmpPerms(ps); setShowEmpModal(true);
  };
  const openEditEmp = (emp) => {
    setEditingEmp(emp);
    const ps = {}; PERM_KEYS.forEach(k => ps[k] = emp[k] ?? false);
    setEmpPerms(ps); setShowEmpModal(true);
  };
  const openAddRole = () => {
    setEditingRole(null);
    const ps = {}; PERM_KEYS.forEach(k => ps[k] = k === 'can_manage_settings' || k === 'can_manage_employees' ? false : true);
    setRolePerms(ps); setShowRoleModal(true);
  };
  const openEditRole = (role) => {
    setEditingRole(role);
    const ps = {}; PERM_KEYS.forEach(k => ps[k] = role[k] || false);
    setRolePerms(ps); setShowRoleModal(true);
  };

  const openWA = () => window.open(`https://wa.me/${data.settings.whatsapp_number?.replace(/\D/g, '')}?text=${encodeURIComponent(data.settings.whatsapp_message || '')}`, '_blank');

  // ══════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════
  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ fontFamily: 'Arial', minHeight: '100vh', background: 'linear-gradient(160deg,#5a67d8 0%,#764ba2 45%,#667eea 100%)' }}>
      <style>{SPIN_CSS}</style>

      {/* Header */}
      <Header
        settings={data.settings} employee={employee} customer={customer}
        lang={lang} page={page} isRTL={isRTL} perms={perms}
        unreadCount={unreadCount} showNotifPanel={showNotifPanel}
        onLogoClick={handleLogoClick}
        onSetPage={setPage}
        onToggleLang={() => setLang(l => l === 'ar' ? 'en' : 'ar')}
        onOpenNotif={openNotifPanel}
        onOpenCustModal={() => { setShowCustModal(true); setCustTab('login'); setCustErr(''); setCustForm({ name: '', email: '', phone: '', password: '', username: '' }); }}
        onLogoutEmp={() => { logEmployeeLogout(employee?.id); try { localStorage.removeItem('emp_token'); } catch {} setEmployee(null); setPage('home'); showToast(lang === 'ar' ? 'تم تسجيل الخروج' : 'Logged out', 'info'); }}
        onLogoutCust={() => { try { localStorage.removeItem('cust_token'); } catch {} setCustomer(null); }}
        t={t}
      />

      {/* News Ticker */}
      <NewsTicker settings={data.settings} onSetPage={setPage} />

      {/* Notification Panel */}
      {showNotifPanel && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 140 }} onClick={() => setShowNotifPanel(false)} />
          <NotifPanel
            pending={pendingPayments} messages={data.contactMessages}
            customers={data.customers} courses={data.courses}
            reviews={data.reviews}
            onApproveReview={async (id) => { await data.approveReview(id); showToast(lang === 'ar' ? 'تم اعتماد الرأي' : 'Approved'); }}
            onDeleteReview={async (id) => { await data.deleteReview(id); showToast(lang === 'ar' ? 'تم رفض الرأي' : 'Rejected', 'warning'); }}
            onClose={() => setShowNotifPanel(false)}
            onApprove={handleApprove} onReject={handleReject}
            isRTL={isRTL} lang={lang} t={t}
          />
        </>
      )}

      {/* Pages + Sidebars */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '16px 12px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>

        {/* Sidebar Right — مخفي على الموبايل */}
        {page === 'home' && (
          <div style={{ display: 'none', position: 'relative', zIndex: 1 }} className="sidebar-right">
            <SidebarPanels side="right" lang={lang} />
          </div>
        )}
        <style>{`
          @media (min-width: 900px) {
            .sidebar-right, .sidebar-left { display: block !important; }
          }
          @media (max-width: 899px) {
            .sidebar-right, .sidebar-left { display: none !important; }
          }
        `}</style>

        {/* Main Content */}
        <div style={{ flex: 1, minWidth: 0 }}>

        {page === 'home' && <Home t={t} isRTL={isRTL} lang={lang} onSetPage={setPage} settings={data.settings} />}

        {page === 'academy' && (
          <Academy
            courses={data.courses} enrollments={data.enrollments} lessons={data.lessons}
            categories={data.categories} instructors={data.instructors} reviews={data.reviews}
            customer={customer} lang={lang} isRTL={isRTL} loading={data.loading} t={t}
            onSetPage={(p, course, lesson) => { if (course) { setActiveCourse(course); setActiveLesson(lesson); } setPage(p); }}
            onOpenInstructor={(instId) => { setFocusInstructorId(instId); setPage('instructors'); }}
            onOpenPayModal={(c) => { if (!customer || customer.isGuest) { showToast(t.loginFirst, 'info'); setShowCustModal(true); setCustTab('login'); return; } setActiveCourse(c); setShowPayModal(true); }}
            onOpenCustModal={(tab) => { setShowCustModal(true); setCustTab(tab); setCustErr(''); setCustForm({ name: '', email: '', phone: '', password: '', username: '' }); }}
          />
        )}

        {page === 'instructors' && (
          <Instructors
            instructors={data.instructors} courses={data.courses} lessons={data.lessons}
            enrollments={data.enrollments} customer={customer}
            lang={lang} isRTL={isRTL} loading={data.loading} t={t}
            focusInstructorId={focusInstructorId}
            clearFocus={() => setFocusInstructorId(null)}
            onSetPage={(p, course, lesson) => { if (course) { setActiveCourse(course); setActiveLesson(lesson); } setPage(p); }}
          />
        )}

        {page === 'player' && activeCourse && (
          <Player
            activeCourse={activeCourse} activeLesson={activeLesson} setActiveLesson={setActiveLesson}
            lessons={data.lessons} lang={lang} isRTL={isRTL} t={t}
            customer={customer} markWatched={data.markWatched} isWatched={data.isWatched}
            studentWorks={data.studentWorks} reviews={data.reviews} enrollments={data.enrollments} instructors={data.instructors}
            isEnrolled={customer && !customer.isGuest ? data.isEnrolled(customer.id, activeCourse.id) : false}
            onEnroll={() => { if (!customer || customer.isGuest) { showToast(t.loginFirst, 'info'); return; } setShowPayModal(true); }}
            onOpenInstructor={(instId) => { setFocusInstructorId(instId); setPage('instructors'); }}
            onAddReview={async (review) => { const res = await data.addReview(review); if (!res?.error) showToast(lang === 'ar' ? 'تم إرسال رأيك للمراجعة' : 'Review submitted', 'success'); return res; }}
            onBack={() => setPage('academy')}
            onBackHome={() => setPage('home')}
          />
        )}

        {page === 'agency' && (
          <Agency services={data.services} serviceReviews={data.serviceReviews} agencyWorks={data.agencyWorks} lang={lang} loading={data.loading} t={t} isRTL={isRTL} onSetPage={setPage} onOpenWA={openWA} onSubmitReview={data.addPublicServiceReview} />
        )}

        {page === 'mycourses' && customer && !customer.isGuest && (
          <MyCourses
            customer={customer} enrollments={data.enrollments} courses={data.courses} lessons={data.lessons}
            lang={lang} t={t} onSetPage={setPage}
            courseProgress={data.courseProgress} isWatched={data.isWatched}
            onGoPlayer={(course, lesson) => { setActiveCourse(course); setActiveLesson(lesson); setPage('player'); }}
          />
        )}

        {page === 'bot-subscribe' && (
          <BotSubscribe
            customer={customer} onSetPage={setPage} showToast={showToast}
            onOpenCustModal={(tab) => { setShowCustModal(true); setCustTab(tab || 'login'); setCustErr(''); }}
          />
        )}

        {page === 'reports' && employee && perms.can_view_dashboard && (
          <Reports
            lang={lang} isRTL={isRTL} t={t}
            payments={data.payments}
            customers={data.customers}
            courses={data.courses}
            lessons={data.lessons}
            enrollments={data.enrollments}
            employees={data.employees}
            onBack={() => setPage('dashboard')}
            onOpenBuilder={() => setPage('report-builder')}
          />
        )}

        {page === 'report-builder' && employee && perms.can_view_dashboard && (
          <ReportBuilder
            payments={data.payments} customers={data.customers}
            courses={data.courses} employees={data.employees}
            enrollments={data.enrollments}
            onBack={() => setPage('reports')}
          />
        )}

        {page === 'contact' && (          <Contact
            settings={data.settings} lang={lang} isRTL={isRTL} t={t}
            onSetPage={setPage} onOpenWA={openWA}
            addContactMessage={data.addContactMessage}
            showToast={showToast}
          />
        )}

        {page === 'about' && (
          <AboutSection
            settings={data.settings} lang={lang} isRTL={isRTL} t={t}
            onSetPage={setPage}
          />
        )}

        {page === 'dashboard' && employee && perms.can_view_dashboard && (
          <Dashboard
            courses={data.courses} services={data.services} customers={data.customers}
            enrollments={data.enrollments} payments={data.payments} employees={data.employees}
            roles={data.roles} contactMessages={data.contactMessages}
            categories={data.categories} instructors={data.instructors}
            reviews={data.reviews}
            onApproveReview={async (id) => { await data.approveReview(id); showToast('تم اعتماد الرأي'); }}
            onDeleteReview={async (id) => { await data.deleteReview(id); showToast('تم حذف الرأي', 'warning'); }}
            lang={lang} isRTL={isRTL} t={t} perms={perms} onSetPage={setPage}
            onOpenSettings={() => setShowSettingsModal(true)}
            onOpenReports={() => setPage('reports')}
            onOpenLabels={() => setShowLabelsManager(true)}
            onOpenKnowledge={() => setShowKnowledge(true)}
            onAddCourse={() => { setEditingCourse(null); setShowCourseModal(true); }}
            onEditCourse={(c) => { setEditingCourse(c); setShowCourseModal(true); }}
            onDeleteCourse={async (id) => { if (!window.confirm(t.confirmDel)) return; await data.deleteCourse(id); showToast(lang === 'ar' ? 'تم حذف الكورس' : 'Deleted', 'warning'); }}
            onManageLessons={(c) => { setManagingCourse(c); setPage('lessons'); }}
            onAddService={() => { setEditingService(null); setShowServiceModal(true); }}
            serviceReviews={data.serviceReviews} agencyWorks={data.agencyWorks}
            onSaveServiceReview={data.saveServiceReview} onDeleteServiceReview={data.deleteServiceReview}
            onApproveServiceReview={data.approveServiceReview}
            onSaveAgencyWork={data.saveAgencyWork} onDeleteAgencyWork={data.deleteAgencyWork}
            onUploadImage={data.uploadImage}
            onEditService={(s) => { setEditingService(s); setShowServiceModal(true); }}
            onDeleteService={async (id) => { if (!window.confirm(t.confirmDel)) return; await data.deleteService(id); showToast(lang === 'ar' ? 'تم حذف الخدمة' : 'Deleted', 'warning'); }}
            onApprove={handleApprove} onReject={handleReject}
            onAddEmp={openAddEmp} onEditEmp={openEditEmp}
            onDeleteEmp={handleDeleteEmp} onToggleEmp={handleToggleEmp}
            onAddRole={openAddRole} onEditRole={openEditRole}
            onDeleteRole={async (id) => { if (!window.confirm(t.delRoleConfirm)) return; await data.deleteRole(id); showToast(lang === 'ar' ? 'تم حذف الوظيفة' : 'Deleted', 'warning'); }}
            onAddCoupon={() => { setEditingCoupon(null); setShowCouponModal(true); }}
            onEditCoupon={(c) => { setEditingCoupon(c); setShowCouponModal(true); }}
            onDeleteCoupon={async (id) => { if (!window.confirm('تأكيد حذف الكوبون؟')) return; const token = (() => { try { return localStorage.getItem('emp_token') || ''; } catch { return ''; } })(); try { const res = await fetch(apiUrl('/api/admin/coupon-delete'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, couponId: id }) }); const r = await res.json().catch(() => ({})); if (!res.ok || !r.ok) { showToast(r.message || 'فشل حذف الكوبون', 'error'); return; } showToast('تم حذف الكوبون', 'warning'); } catch { showToast('فشل الاتصال بالسيرفر', 'error'); } }}
            onToggleCoupon={async (c) => { const token = (() => { try { return localStorage.getItem('emp_token') || ''; } catch { return ''; } })(); try { const res = await fetch(apiUrl('/api/admin/coupon-toggle'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, couponId: c.id, isActive: !c.is_active }) }); const r = await res.json().catch(() => ({})); if (!res.ok || !r.ok) { showToast(r.message || 'فشل تحديث الكوبون', 'error'); return; } showToast(c.is_active ? 'تم إيقاف الكوبون' : 'تم تفعيل الكوبون'); } catch { showToast('فشل الاتصال بالسيرفر', 'error'); } }}
            onToggleCustomer={handleToggleCustomer}
            onDeleteCustomer={handleDeleteCustomer}
            onCreateCustomer={data.createCustomer}
            onUpdateCustomer={data.updateCustomer}
            onSetCustomerPassword={data.setCustomerPassword}
            showToast={showToast}
            onAddCategory={() => { setEditingCategory(null); setShowCategoryModal(true); }}
            onEditCategory={(c) => { setEditingCategory(c); setShowCategoryModal(true); }}
            onDeleteCategory={async (id) => { if (!window.confirm('تأكيد حذف التصنيف؟')) return; await data.deleteCategory(id); showToast('تم حذف التصنيف', 'warning'); }}
            onAddInstructor={() => { setEditingInstructor(null); setShowInstructorModal(true); }}
            onEditInstructor={(i) => { setEditingInstructor(i); setShowInstructorModal(true); }}
            onDeleteInstructor={async (id) => { if (!window.confirm('تأكيد حذف المدرب؟')) return; await data.deleteInstructor(id); showToast('تم حذف المدرب', 'warning'); }}
          />
        )}

        {page === 'lessons' && managingCourse && employee && (
          <LessonsManager
            managingCourse={managingCourse} lessons={data.lessons}
            lang={lang} isRTL={isRTL} t={t}
            onBack={() => setPage('dashboard')}
            onAddLesson={() => { setEditingLesson(null); setShowLessonModal(true); }}
            onEditLesson={async (l) => { const token = (() => { try { return localStorage.getItem('emp_token') || ''; } catch { return ''; } })(); let full = l; try { const res = await fetch(apiUrl('/api/admin/lesson-full'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, lessonId: l.id }) }); const r = await res.json().catch(() => ({})); if (res.ok && r.ok && r.lesson) full = r.lesson; } catch (e) {} setEditingLesson(full); setShowLessonModal(true); }}
            onDeleteLesson={async (id) => { if (!window.confirm(t.confirmDel)) return; const dr = await data.deleteLesson(id); if (dr && dr.error) { showToast(dr.error, 'error'); return; } showToast(lang === 'ar' ? 'تم حذف الحلقة' : 'Deleted', 'warning'); }}
            studentWorks={data.studentWorks}
            onSaveWork={async (workData, editing) => { await data.saveStudentWork(workData, editing); showToast(editing ? 'تم تعديل العمل' : 'تم إضافة العمل'); }}
            onDeleteWork={async (id) => { await data.deleteStudentWork(id); showToast('تم حذف العمل', 'warning'); }}
            onUploadImage={data.uploadImage}
          />
        )}
        </div>{/* end main content */}

        {/* Sidebar Left — مخفي على الموبايل */}
        {page === 'home' && (
          <div style={{ display: 'none', position: 'relative', zIndex: 1 }} className="sidebar-left">
            <SidebarPanels side="left" lang={lang} />
          </div>
        )}

      </div>{/* end pages+sidebars */}

      {/* ═══ MODALS ═══ */}
      {showEmpLogin && (
        <EmpLoginModal t={t} isRTL={isRTL} onSubmit={handleEmpLogin} onClose={() => setShowEmpLogin(false)}
          empErr={empErr} empOk={empOk} empLoad={empLoad}
          empUsernameRef={empUsernameRef} empPasswordRef={empPasswordRef} />
      )}

      {showCustModal && (
        <CustModal t={t} isRTL={isRTL} onSubmit={handleCustSubmit} onClose={() => setShowCustModal(false)}
          custTab={custTab} setCustTab={setCustTab}
          custForm={custForm} setCustForm={setCustForm}
          custErr={custErr} setCustErr={setCustErr}
          custOk={custOk} custLoad={custLoad}
          onForgotPassword={() => { setShowCustModal(false); setShowForgotPass(true); }} />
      )}

      {showPayModal && activeCourse && (
        <PaymentModal
          activeCourse={activeCourse} customer={customer} settings={data.settings}
          t={t} lang={lang} onClose={() => setShowPayModal(false)}
          onCardPay={startCardPayment} onManualPay={startManualPayment} onFreeEnroll={startFreeEnroll}
          payLoad={payLoad} payErr={payErr} />
      )}

      {showCourseModal && (
        <CourseModal editing={editingCourse} t={t} onSubmit={submitCourse} onClose={() => { setShowCourseModal(false); setEditingCourse(null); }} saveLoad={saveLoad} onUploadThumbnail={data.uploadThumbnail} onUploadImage={data.uploadImage} categories={data.categories} instructors={data.instructors} />
      )}

      {showLessonModal && managingCourse && (
        <LessonModal editing={editingLesson} managingCourse={managingCourse} lessons={data.lessons} t={t}
          onSubmit={submitLesson} onClose={() => { setShowLessonModal(false); setEditingLesson(null); }} saveLoad={saveLoad} onUploadFile={data.uploadFile} />
      )}

      {showServiceModal && (
        <ServiceModal editing={editingService} t={t} onSubmit={submitService} onClose={() => { setShowServiceModal(false); setEditingService(null); }} saveLoad={saveLoad} onUploadImage={data.uploadImage} />
      )}

      {showRoleModal && (
        <RoleModal editing={editingRole} t={t} onSubmit={submitRole} onClose={() => { setShowRoleModal(false); setEditingRole(null); }} saveLoad={saveLoad} rolePerms={rolePerms} setRolePerms={setRolePerms} />
      )}

      {showEmpModal && (
        <EmployeeModal editing={editingEmp} t={t} lang={lang} onSubmit={submitEmployee} onClose={() => { setShowEmpModal(false); setEditingEmp(null); }} saveLoad={saveLoad} empPerms={empPerms} setEmpPerms={setEmpPerms} roles={data.roles} />
      )}

      {showSettingsModal && (
        <SettingsModal settings={data.settings} t={t} onSubmit={submitSettings} onClose={() => setShowSettingsModal(false)} saveLoad={saveLoad} onUploadImage={data.uploadImage} />
      )}

      {showCouponModal && (
        <CouponModal
          editing={editingCoupon}
          onSubmit={submitCoupon}
          onClose={() => { setShowCouponModal(false); setEditingCoupon(null); }}
          saveLoad={saveLoad}
          courses={data.courses}
          lang={lang}
        />
      )}

      {showCategoryModal && (
        <CategoryModal
          editing={editingCategory}
          onSubmit={submitCategory}
          onClose={() => { setShowCategoryModal(false); setEditingCategory(null); }}
          saveLoad={saveLoad}
        />
      )}

      {showInstructorModal && (
        <InstructorModal
          editing={editingInstructor}
          onSubmit={submitInstructor}
          onClose={() => { setShowInstructorModal(false); setEditingInstructor(null); }}
          saveLoad={saveLoad}
          onUploadImage={data.uploadImage}
        />
      )}

      {showForgotPass && (
        <ForgotPasswordModal
          onClose={() => setShowForgotPass(false)}
          onBackToLogin={() => { setShowForgotPass(false); setShowCustModal(true); setCustTab('login'); }}
        />
      )}

      {showLabelsManager && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}
          onClick={() => setShowLabelsManager(false)}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '800px', marginTop: '20px' }}>
            <LabelsManager
              onClose={() => setShowLabelsManager(false)}
              onSaved={refreshLabels}
              lang={lang}
            />
          </div>
        </div>
      )}

      {showKnowledge && (
        <BotManager
          lang={lang} isRTL={isRTL}
          customers={data.customers} perms={perms}
          onClose={() => setShowKnowledge(false)}
        />
      )}

      {/* Floating Buttons */}
      <FloatingButtons settings={data.settings} isRTL={isRTL} lang={lang} courses={data.courses} services={data.services} t={t}
        customer={customer}
        onOpenCustModal={(tab) => { setShowCustModal(true); setCustTab(tab || 'login'); setCustErr(''); setCustForm({ name: '', email: '', phone: '', password: '', username: '' }); }}
        onSetPage={setPage} />

      {/* Toasts */}
      <ToastContainer toasts={toasts} removeToast={removeToast} isRTL={isRTL} />
    </div>
  );
}
